import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import classnames from "classnames";
import Swal from "sweetalert2";
import { getVendorSettings, setVendorData } from "../../services/helper";
import http from "../../services/http";

const ExternalInventory = () => {
  const [inventory, setInventory] = useState({});
  const [categories, setCategories] = useState({});
  const [newFlavor, setNewFlavor] = useState("");
  const [activeTab, setActiveTab] = useState("");
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryLabel, setNewCategoryLabel] = useState("");

  useEffect(() => {
    try {
      const savedInventory = getVendorSettings("external_inventory");
      const savedCategories = getVendorSettings("inventory_categories");

      let parsedCategories = savedCategories ? JSON.parse(savedCategories) : {};

      let parsedInventory = savedInventory ? JSON.parse(savedInventory) : {};

      Object.keys(parsedCategories).forEach((key) => {
        if (!parsedInventory[key]) parsedInventory[key] = [];
      });

      setCategories(parsedCategories);
      setInventory(parsedInventory);
      setActiveTab(Object.keys(parsedCategories)[0] || "");
    } catch (e) {
      console.error("Failed to parse saved data", e);
      const defaultCategories = {
        boba: "Boba Inventory",
        gelato: "Gelato Inventory",
        cups: "Cup Inventory",
      };
      setCategories(defaultCategories);
      setInventory({ boba: [], gelato: [], cups: [] });
      setActiveTab("boba");
    }
  }, []);

  const saveInventory = async (updated) => {
    setInventory(updated);
    const dataStr = JSON.stringify(updated);
    setVendorData({ external_inventory: dataStr });
    await http.saveVendorData({ external_inventory: dataStr });
  };

  const saveCategories = async (updated) => {
    setCategories(updated);
    const dataStr = JSON.stringify(updated);
    setVendorData({ inventory_categories: dataStr });
    await http.saveVendorData({ inventory_categories: dataStr });
  };

  const handleAddCategory = async () => {
    if (!newCategoryLabel.trim()) return;

    const key = newCategoryLabel
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
    const updatedCategories = { ...categories, [key]: newCategoryLabel.trim() };
    const updatedInventory = { ...inventory, [key]: [] };

    await saveCategories(updatedCategories);
    await saveInventory(updatedInventory);

    setNewCategoryLabel("");
    setShowCategoryModal(false);
  };

  const handleRemoveCategory = async (categoryKey) => {
    const { isConfirmed } = await Swal.fire({
      title: "Remove Category",
      text: `Are you sure you want to remove "${categories[categoryKey]}"? This will delete all items in this category.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove it",
      cancelButtonText: "Cancel",
    });
    
    if (!isConfirmed) return;

    const updatedCategories = { ...categories };
    const updatedInventory = { ...inventory };
    
    delete updatedCategories[categoryKey];
    delete updatedInventory[categoryKey];

    await saveCategories(updatedCategories);
    await saveInventory(updatedInventory);

    const remainingKeys = Object.keys(updatedCategories);
    setActiveTab(remainingKeys[0] || "");
  };

  const handleChange = (category, index, field, value) => {
    const updated = { ...inventory };
    if (field === "updatedAt") {
      updated[category][index][field] = new Date(value);
    } else {
      updated[category][index][field] = parseFloat(value) || 0;
      updated[category][index].updatedAt = new Date();
    }
    saveInventory(updated);
  };

  const handleAddFlavor = () => {
    if (!newFlavor.trim()) return;

    const newItem = {
      flavor: newFlavor.trim(),
      downstairs: 0,
      upstairs: 0,
      onOrder: 0,
      updatedAt: new Date(),
    };

    const updated = { ...inventory };

    // ✅ Ensure category exists
    if (!updated[activeTab]) {
      updated[activeTab] = [];
    }

    updated[activeTab].push(newItem);
    saveInventory(updated);
    setNewFlavor("");
  };

  const handleRemoveFlavor = (category, index) => {
    const confirmed = window.confirm(
      "Are you sure you want to remove this flavor?"
    );
    if (!confirmed) return;

    const updated = { ...inventory };
    updated[category].splice(index, 1);
    saveInventory(updated);
  };

  const getTotal = (items, key) =>
    items.reduce((acc, row) => acc + (parseFloat(row[key]) || 0), 0);

  const formatDateInput = (date) => {
    const d = new Date(date);
    const pad = (n) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  return (
    <div className="p-3">
      <h4>External Inventory</h4>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Nav tabs>
          {Object.entries(categories).map(([key, label]) => (
            <NavItem key={key}>
              <NavLink
                className={classnames({ active: activeTab === key })}
                onClick={() => setActiveTab(key)}
                style={{ cursor: "pointer" }}
              >
                {label}
                {Object.keys(categories).length > 1 && (
                  <Button
                    color="danger"
                    size="sm"
                    className="ms-2 p-0"
                    style={{ width: "20px", height: "20px", fontSize: "12px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCategory(key);
                    }}
                  >
                    ×
                  </Button>
                )}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
        <Button
          color="success"
          size="sm"
          onClick={() => setShowCategoryModal(true)}
        >
          Add Category
        </Button>
      </div>

      <TabContent activeTab={activeTab}>
        {Object.keys(categories).map((category) => (
          <TabPane tabId={category} key={category} className="pt-3">
            <Row className="mb-3">
              <Col md={6}>
                <Input
                  placeholder={`Enter new flavor for ${categories[category]}`}
                  value={activeTab === category ? newFlavor : ""}
                  onChange={(e) => setNewFlavor(e.target.value)}
                />
              </Col>
              <Col md="auto">
                <Button color="primary" onClick={handleAddFlavor}>
                  Add Flavor
                </Button>
              </Col>
            </Row>

            <Table bordered responsive>
              <thead className="table-light">
                <tr>
                  <th>Flavor</th>
                  <th>Downstairs Stock</th>
                  <th>Upstairs Stock</th>
                  <th>On Order</th>
                  <th>Last Updated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(inventory[category] || []).map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.flavor}</td>
                    <td>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.downstairs}
                        onChange={(e) =>
                          handleChange(
                            category,
                            idx,
                            "downstairs",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.upstairs}
                        onChange={(e) =>
                          handleChange(
                            category,
                            idx,
                            "upstairs",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.onOrder}
                        onChange={(e) =>
                          handleChange(category, idx, "onOrder", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Input
                        type="datetime-local"
                        value={formatDateInput(item.updatedAt)}
                        onChange={(e) =>
                          handleChange(
                            category,
                            idx,
                            "updatedAt",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveFlavor(category, idx)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-secondary fw-bold">
                <tr>
                  <td>Total</td>
                  <td>
                    {getTotal(inventory[category] || [], "downstairs").toFixed(
                      2
                    )}
                  </td>
                  <td>
                    {getTotal(inventory[category] || [], "upstairs").toFixed(2)}
                  </td>
                  <td>
                    {getTotal(inventory[category] || [], "onOrder").toFixed(2)}
                  </td>
                  <td colSpan="2">—</td>
                </tr>
              </tfoot>
            </Table>
          </TabPane>
        ))}
      </TabContent>

      <Modal
        isOpen={showCategoryModal}
        toggle={() => setShowCategoryModal(false)}
      >
        <ModalHeader toggle={() => setShowCategoryModal(false)}>
          Add New Category
        </ModalHeader>
        <ModalBody>
          <Input
            placeholder="Category Name (e.g., Drinks Inventory)"
            value={newCategoryLabel}
            onChange={(e) => setNewCategoryLabel(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddCategory}>
            Add Category
          </Button>
          <Button color="danger" onClick={() => setShowCategoryModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ExternalInventory;
